import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  LuBuilding2,
  LuFileText,
  LuGlobe,
  LuIdCard,
  LuMapPin,
  LuPencil,
  LuPhone,
  LuUsers,
} from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { EmptyNote, InfoItem, InfoSection as Section, ManageLink } from '../../components/ui/InfoBlocks';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';
import { StatusBadge, customerInitials } from './components/CustomerCell';
import { customerName, formatIsoDate, formatPaymentPeriod, getFilterValueLabel, isEmptyValue } from './customerColumns';

// Yetkili kişilerden kartta gösterilen en fazla kişi
const PERSONNEL_SHOWN = 4;

const display = (value) => (isEmptyValue(value) || String(value).trim() === '' ? '—' : String(value).trim());

/**
 * "Müşteri Kartı": everything about a customer at a glance (read only), with
 * the counts of its projects, contacts and tariffs.
 */
const CustomerCardModal = ({ customer, onClose, onEdit, onOpenAddresses, onOpenPersonnel }) => {
  const { t, language, locale } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState({ addresses: [], personnel: [], tariffCount: 0, projects: [] });

  useEffect(() => {
    if (!customer?.id) return undefined;
    let cancelled = false;

    const fetchRelated = async () => {
      setLoading(true);
      try {
        const name = customerName(customer);
        const [addressSnap, personnelSnap, tariffSnap, projectSnap] = await Promise.all([
          getDocs(query(collection(db, 'CustomerAddresses'), where('customerId', '==', customer.id))),
          getDocs(query(collection(db, 'CustomerAuthorizedPersonnel'), where('customerId', '==', customer.id))),
          getDocs(query(collection(db, 'CustomerTariffs'), where('customerId', '==', customer.id))),
          // Projeler müşteriye isim üzerinden bağlanıyor (Projects.customer = Customers.Başlık)
          name ? getDocs(query(collection(db, 'Projects'), where('customer', '==', name))) : Promise.resolve({ docs: [] }),
        ]);
        if (cancelled) return;
        setRelated({
          addresses: addressSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
          personnel: personnelSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
          tariffCount: tariffSnap.docs.length,
          projects: projectSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        });
      } catch (error) {
        console.error('Error fetching customer card data:', error);
        if (!cancelled) toast.error(t('customers.card.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRelated();
    return () => {
      cancelled = true;
    };
    // Reload only for another customer, not when the language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  if (!customer) return null;

  const name = customerName(customer);
  const defaultAddress = related.addresses.find((address) => address.isDefault) || related.addresses[0] || null;
  const activeProjects = related.projects.filter((project) => (project.projectStatus || 'Aktif') === 'Aktif').length;
  const domestic = isEmptyValue(customer['Yurtiçi mi?']) ? '' : String(customer['Yurtiçi mi?']);
  const currency = isEmptyValue(customer['Döviz Tipi']) ? '' : String(customer['Döviz Tipi']);
  const serviceNumber = display(customer['Müşteri Hizmet Num.']);
  const extraPersonnel = related.personnel.length - PERSONNEL_SHOWN;

  const stats = [
    { key: 'projects', label: t('customers.card.totalProjects'), value: related.projects.length },
    { key: 'active', label: t('customers.card.activeProjects'), value: activeProjects },
    { key: 'people', label: t('customers.card.people'), value: related.personnel.length },
    { key: 'tariffs', label: t('customers.card.tariffs'), value: related.tariffCount },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="form"
      title={t('customers.card.title')}
      icon={(
        <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30" size="xs">
          <LuIdCard aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-p-0"
      footer={(
        <>
          <Button variant="ghost" onClick={() => onClose()}>{t('common.close')}</Button>
          {onEdit && (
            <Button variant="primary" onClick={onEdit}>
              <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
              {t('customers.card.edit')}
            </Button>
          )}
        </>
      )}
    >
      <div className="md:tw-grid md:tw-grid-cols-[17rem_minmax(0,1fr)]">
        {/* Identity and summary */}
        <aside className="tw-space-y-4 tw-border-b tw-border-slate-200/80 tw-bg-slate-50/70 tw-p-5 md:tw-border-b-0 md:tw-border-r dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
          <div className="tw-flex tw-items-center tw-gap-4 md:tw-flex-col md:tw-text-center">
            <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30" size="avatarXl" className="md:tw-h-20 md:tw-w-20 md:tw-text-2xl">
              {customerInitials(name)}
            </GlassIcon>
            <div className="tw-min-w-0">
              <h2 className="tw-break-words tw-text-lg tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">{display(name)}</h2>
              {display(customer['Bilgi']) !== '—' && (
                <p className="tw-mt-1 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{display(customer['Bilgi'])}</p>
              )}
              <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-gap-1.5 md:tw-justify-center">
                <StatusBadge value={customer['Durum'] ? String(customer['Durum']) : undefined} />
                {currency && <Badge tone="neutral">{currency}</Badge>}
                {domestic && (
                  <Badge tone="info">
                    <LuGlobe aria-hidden="true" className="tw-h-3 tw-w-3" />
                    {domestic === 'Evet' ? t('customers.domestic') : t('customers.abroad')}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <dl aria-label={t('customers.card.summary')} className="tw-grid tw-grid-cols-2 tw-gap-2">
            {stats.map((stat) => (
              <div key={stat.key} className="tw-rounded-2xl tw-border tw-border-white/80 tw-bg-white/80 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.04]">
                <dt className="tw-truncate tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{stat.label}</dt>
                <dd className="tw-mt-0.5 tw-text-xl tw-font-semibold tw-text-slate-900 tw-tabular-nums dark:tw-text-white">
                  {loading
                    ? <span aria-hidden="true" className="tw-block tw-h-6 tw-w-8 tw-animate-pulse tw-rounded-md tw-bg-slate-200/80 dark:tw-bg-white/10" />
                    : stat.value.toLocaleString(locale)}
                </dd>
              </div>
            ))}
          </dl>

          {serviceNumber !== '—' && (
            <a
              href={`tel:${serviceNumber.replace(/\s+/g, '')}`}
              className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-white/80 tw-bg-white/80 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-800 tw-transition-colors hover:tw-text-teal-700 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-100 dark:hover:tw-text-teal-300"
            >
              <LuPhone aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
              <span className="tw-min-w-0 tw-flex-1">
                <span className="tw-block tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{t('customers.columns.serviceNumber')}</span>
                <span className="tw-block tw-truncate">{serviceNumber}</span>
              </span>
            </a>
          )}
        </aside>

        {/* Details */}
        <div className="tw-space-y-4 tw-p-3 sm:tw-p-4">
          <Section icon={LuBuilding2} title={t('customers.card.company')}>
            <InfoItem label={t('customers.card.name')} value={display(name)} copyable />
            <InfoItem label={t('customers.columns.taxNumber')} value={display(customer['Vergi Numarası'])} copyable />
            <InfoItem label={t('customers.columns.domestic')} value={domestic ? getFilterValueLabel(t, 'Yurtiçi mi?', domestic) : '—'} />
            <InfoItem label={t('customers.columns.currency')} value={display(customer['Döviz Tipi'])} />
            <InfoItem
              label={t('customers.columns.serviceNumber')}
              value={serviceNumber}
              href={serviceNumber !== '—' ? `tel:${serviceNumber}` : undefined}
              copyable
            />
            <InfoItem label={t('customers.columns.status')} value={display(customer['Durum'])} />
            <InfoItem label={t('customers.columns.info')} value={display(customer['Bilgi'])} wide />
          </Section>

          <Section icon={LuFileText} title={t('customers.card.agreement')}>
            <InfoItem label={t('customers.columns.agreementNumber')} value={display(customer['Ana Anlaşma Numarası'])} copyable />
            <InfoItem label={t('customers.columns.agreementDate')} value={display(formatIsoDate(customer['Ana Anlaşma Tarihi'], locale))} />
            <InfoItem label={t('customers.columns.deliveryDate')} value={display(formatIsoDate(customer['Teslim Tarihi'], locale))} />
            <InfoItem label={t('customers.columns.paymentPeriod')} value={display(formatPaymentPeriod(t, customer['Ödeme Periyodu']))} />
          </Section>

          <Section
            icon={LuMapPin}
            title={t('customers.card.addresses', { count: related.addresses.length })}
            action={onOpenAddresses && <ManageLink label={t('customers.card.manage')} onClick={onOpenAddresses} />}
          >
            {loading && <EmptyNote>{t('customers.common.loading')}</EmptyNote>}
            {!loading && !defaultAddress && <EmptyNote>{t('customers.card.noAddress')}</EmptyNote>}
            {!loading && defaultAddress && (
              <>
                <InfoItem
                  label={defaultAddress.isDefault ? t('customers.card.defaultAddress') : t('customers.card.address')}
                  value={display(defaultAddress.title)}
                />
                <InfoItem
                  label={t('customers.card.cityDistrict')}
                  value={display([defaultAddress.city, defaultAddress.district].filter(Boolean).join(' / '))}
                />
                <InfoItem label={t('customers.card.fullAddress')} value={display(defaultAddress.address)} wide />
              </>
            )}
          </Section>

          <Section
            icon={LuUsers}
            title={t('customers.card.personnel', { count: related.personnel.length })}
            action={onOpenPersonnel && <ManageLink label={t('customers.card.manage')} onClick={onOpenPersonnel} />}
          >
            {loading && <EmptyNote>{t('customers.common.loading')}</EmptyNote>}
            {!loading && related.personnel.length === 0 && <EmptyNote>{t('customers.card.noPersonnel')}</EmptyNote>}
            {!loading && related.personnel.slice(0, PERSONNEL_SHOWN).map((person) => (
              <InfoItem
                key={person.id}
                label={display(person.position) !== '—' ? person.position : t('customers.card.personFallback')}
                value={display(`${person.firstName || ''} ${person.lastName || ''}`.trim())}
                hint={display(person.phone) !== '—' ? person.phone : undefined}
              />
            ))}
            {!loading && extraPersonnel > 0 && <EmptyNote>{t('customers.card.morePersonnel', { count: extraPersonnel })}</EmptyNote>}
          </Section>
        </div>
      </div>
    </Modal>
  );
};

CustomerCardModal.propTypes = {
  customer: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onOpenAddresses: PropTypes.func,
  onOpenPersonnel: PropTypes.func,
};

export default CustomerCardModal;
